import { ExcoActions } from './exco.enums';
import { SubscriptionKey } from './exco.types';
import { IOpenClient } from './interfaces/exco-client.interface';
import { ExcoPlacementIFrame } from './interfaces/exco-placement-iframe.interface';
import { querySelectorAllWatcher } from './utils/dom-watcher';

export class EXCO_Client implements IOpenClient {
  private readonly _PLACEMENTS_WINDOW_PROPERTY: string = 'excoPlacements';
  private excoPlacements: Array<string>;

  constructor() {
    this.excoPlacements = window[this._PLACEMENTS_WINDOW_PROPERTY] =
      window[this._PLACEMENTS_WINDOW_PROPERTY] || [];
  }

  public publish(
    iframe: ExcoPlacementIFrame,
    action: ExcoActions,
    data: any
  ): void {
    try {
      return iframe.contentWindow.pbPubsub.publish(action, data);
    } catch (error) {
      console.error(error);
    }
  }
  public subscribe(
    iframe: ExcoPlacementIFrame,
    action: ExcoActions,
    callback: Function
  ): SubscriptionKey {
    try {
      return iframe.contentWindow.pbPubsub.subscribe(action, callback);
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  public unsubscribe(iframe: ExcoPlacementIFrame, key: SubscriptionKey): void {
    try {
      iframe.contentWindow.pbPubsub.unsubscribe(key);
    } catch (error) {
      console.error(error);
    }
  }

  public mount(
    placementId: string,
    target: HTMLElement = document.body
  ): Promise<ExcoPlacementIFrame> {
    this._clearPlacementsById(placementId);

    this._appendPlacementBlockToTarget(placementId, target);

    this.excoPlacements.push(placementId);

    // temporary fix for push decorator in sdk.js
    if (!this.excoPlacements.includes(placementId)) {
      Array.prototype.push.call(this.excoPlacements, placementId);
    }

    return this._getIframeCreationPromise(placementId, target);
  }

  public unmount(placementId: string): void {
    const removeIndex = this.excoPlacements.indexOf(placementId);

    if (removeIndex === -1) {
      throw Error(`palcementId: ${placementId} is not mounted`);
    }

    this.excoPlacements.splice(removeIndex, 1);

    this._clearPlacementsById(placementId);
  }

  private _clearPlacementsById(placementId): void {
    const placements = document.querySelectorAll(
      `div[data-placement-id="${placementId}"]`
    );

    for (let place of placements) {
      place.remove();
    }
  }

  private _getIframeCreationPromise(
    placementId: string,
    target: HTMLElement = document.body
  ): Promise<ExcoPlacementIFrame> {
    return new Promise<ExcoPlacementIFrame>((resolve, reject) => {
      try {
        const watcher = querySelectorAllWatcher(
          target,
          `iframe[data-placement-id="${placementId}"]`,
          (node: ExcoPlacementIFrame) => {
            let innerValue = undefined;
            Object.defineProperty(node.contentWindow, 'pbPubsub', {
              set(v) {
                innerValue = v;
                watcher.unsubscribe();
                resolve(node);
              },
              get() {
                return innerValue;
              },
            });
          }
        );
      } catch (error) {
        reject(error);
      }
    });
  }

  private _appendPlacementBlockToTarget(
    placementId: string,
    target: HTMLElement
  ): void {
    const block = document.createElement('div');

    block.dataset.container = 'exco-placement';
    block.dataset.placementId = placementId;

    try {
      target.appendChild(block);
    } catch (error) {
      throw Error(`Can't append block to ${target}`);
    }
  }
}
